/**
 * Protected route component for React Router
 * Guards routes that require authentication
 */

import { type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "./auth-provider";

interface ProtectedProps {
  children: ReactElement;
  fallback?: ReactElement;
  redirectTo?: string;
}

/**
 * Protected route wrapper
 *
 * Checks authentication status and redirects to login if not authenticated
 * Shows loading state while checking auth
 */
export function Protected({
  children,
  fallback,
  redirectTo = "/login",
}: ProtectedProps): ReactElement | null {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return fallback || <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`${redirectTo}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return children;
}

/**
 * Component that only renders when user is signed in
 */
export function SignedIn({ children }: { children: ReactElement }): ReactElement | null {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return children;
}

/**
 * Component that only renders when user is signed out
 */
export function SignedOut({ children }: { children: ReactElement }): ReactElement | null {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading || isAuthenticated) {
    return null;
  }

  return children;
}
